import { Resolver, Mutation, Arg, Ctx, Directive } from 'type-graphql'
import { In } from 'typeorm'
import { getRepository } from '@things-factory/shell'
import type ResolverContext from '@things-factory/auth-base'

import { KpiStatistic } from './kpi-statistic.js'
import { NewKpiStatistic, KpiStatisticPatch } from './kpi-statistic-type.js'
import { KpiStatisticCalculationService } from './kpi-statistic-calculation.service.js'
import { KpiPeriodType } from '../kpi/kpi.js'

@Resolver(KpiStatistic)
export class KpiStatisticMutation {
  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => KpiStatistic, { description: 'To create new KpiStatistic' })
  async createKpiStatistic(
    @Arg('kpiStatistic') kpiStatistic: NewKpiStatistic,
    @Ctx() context: ResolverContext
  ): Promise<KpiStatistic> {
    const { domain, user, tx } = context.state

    // 메타데이터 자동 설정
    const enrichedKpiStatistic = {
      ...kpiStatistic,
      metadata: {
        calculationMethod: kpiStatistic.metadata?.calculationMethod || 'manual',
        lastCalculated: new Date(),
        dataCount: kpiStatistic.count || 0
      }
    }

    const result = await getRepository(KpiStatistic, tx).save({
      ...enrichedKpiStatistic,
      domain,
      creator: user,
      updater: user
    })

    return result
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => KpiStatistic, { description: 'To modify KpiStatistic information' })
  async updateKpiStatistic(
    @Arg('id') id: string,
    @Arg('patch') patch: KpiStatisticPatch,
    @Ctx() context: ResolverContext
  ): Promise<KpiStatistic> {
    const { domain, user, tx } = context.state

    const repository = getRepository(KpiStatistic, tx)
    const kpiStatistic = await repository.findOne({
      where: { domain: { id: domain.id }, id }
    })

    // 메타데이터 업데이트
    const updatedMetadata = {
      ...kpiStatistic.metadata,
      ...patch.metadata,
      lastCalculated: new Date()
    }

    const result = await repository.save({
      ...kpiStatistic,
      ...patch,
      metadata: updatedMetadata,
      updater: user
    })

    return result
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => [KpiStatistic], { description: "To modify multiple KpiStatistics' information" })
  async updateMultipleKpiStatistic(
    @Arg('patches', type => [KpiStatisticPatch]) patches: KpiStatisticPatch[],
    @Ctx() context: ResolverContext
  ): Promise<KpiStatistic[]> {
    const { domain, user, tx } = context.state

    let results = []
    const _createRecords = patches.filter((patch: any) => patch.cuFlag.toUpperCase() === '+')
    const _updateRecords = patches.filter((patch: any) => patch.cuFlag.toUpperCase() === 'M')
    const kpiStatisticRepo = getRepository(KpiStatistic, tx)

    if (_createRecords.length > 0) {
      for (let i = 0; i < _createRecords.length; i++) {
        const newRecord = _createRecords[i]

        // 메타데이터 자동 설정
        const enrichedRecord = {
          ...newRecord,
          metadata: {
            calculationMethod: newRecord.metadata?.calculationMethod || 'manual',
            lastCalculated: new Date(),
            dataCount: newRecord.count || 0
          }
        }

        const result = await kpiStatisticRepo.save({
          ...enrichedRecord,
          domain,
          creator: user,
          updater: user
        })

        results.push({ ...result, cuFlag: '+' })
      }
    }

    if (_updateRecords.length > 0) {
      for (let i = 0; i < _updateRecords.length; i++) {
        const updateRecord = _updateRecords[i]
        const kpiStatistic = await kpiStatisticRepo.findOne({
          where: { domain: { id: domain.id }, id: updateRecord.id }
        })

        if (!kpiStatistic) {
          throw new Error(`failed to find kpiStatistic to update: ${updateRecord.id}`)
        }

        // 메타데이터 업데이트
        const updatedMetadata = {
          ...kpiStatistic.metadata,
          ...updateRecord.metadata,
          lastCalculated: new Date()
        }

        const result = await kpiStatisticRepo.save({
          ...kpiStatistic,
          ...updateRecord,
          metadata: updatedMetadata,
          updater: user
        })

        results.push({ ...result, cuFlag: 'M' })
      }
    }

    return results
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Boolean, { description: 'To delete KpiStatistic' })
  async deleteKpiStatistic(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<boolean> {
    const { domain, tx } = context.state

    await getRepository(KpiStatistic, tx).delete({ domain: { id: domain.id }, id })

    return true
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Boolean, { description: 'To delete multiple KpiStatistics' })
  async deleteKpiStatistics(
    @Arg('ids', type => [String]) ids: string[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, tx } = context.state

    await getRepository(KpiStatistic, tx).delete({
      domain: { id: domain.id },
      id: In(ids)
    })

    return true
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Boolean, { description: 'To import KpiStatistics' })
  async importKpiStatistics(
    @Arg('kpiStatistics', type => [KpiStatisticPatch]) kpiStatistics: KpiStatisticPatch[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, user, tx } = context.state

    for (const kpiStatistic of kpiStatistics) {
      // 메타데이터 자동 설정
      const enrichedKpiStatistic = {
        ...kpiStatistic,
        metadata: {
          calculationMethod: kpiStatistic.metadata?.calculationMethod || 'import',
          lastCalculated: new Date(),
          dataCount: kpiStatistic.count || 0
        }
      }

      await getRepository(KpiStatistic, tx).save({
        ...enrichedKpiStatistic,
        domain,
        creator: user,
        updater: user
      })
    }

    return true
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => [KpiStatistic], { 
    description: 'Calculate statistics for a specific KPI and period from KpiValue data' 
  })
  async calculateKpiStatistics(
    @Arg('kpiId') kpiId: string,
    @Arg('periodType', type => KpiPeriodType) periodType: KpiPeriodType,
    @Arg('valueDate') valueDate: string,
    @Ctx() context: ResolverContext
  ): Promise<KpiStatistic[]> {
    const overallStats = await KpiStatisticCalculationService.calculateKpiStatistics(
      kpiId,
      periodType,
      valueDate,
      null, // 전체 통계
      context
    )

    const scopedStats = await KpiStatisticCalculationService.calculateScopedStatistics(
      kpiId,
      periodType,
      valueDate,
      context
    )

    return [overallStats, ...scopedStats].filter(Boolean)
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => [KpiStatistic], { 
    description: 'Calculate statistics for all KPIs in a specific period from KpiValue data' 
  })
  async calculateAllKpiStatistics(
    @Arg('periodType', type => KpiPeriodType) periodType: KpiPeriodType,
    @Arg('valueDate') valueDate: string,
    @Ctx() context: ResolverContext
  ): Promise<KpiStatistic[]> {
    return await KpiStatisticCalculationService.calculateAllStatistics(
      periodType,
      valueDate,
      context
    )
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => [KpiStatistic], { 
    description: 'Recalculate statistics for dashboard regions (scope02-based statistics)' 
  })
  async calculateRegionalStatistics(
    @Arg('periodType', type => KpiPeriodType, { defaultValue: KpiPeriodType.MONTH }) periodType: KpiPeriodType,
    @Arg('valueDate') valueDate: string,
    @Ctx() context: ResolverContext
  ): Promise<KpiStatistic[]> {
    const { domain, user, tx } = context.state
    
    // 지역별(scope02) 통계만 계산
    const kpiStatistics = await getRepository(KpiStatistic, tx)
      .createQueryBuilder('stat')
      .leftJoinAndSelect('stat.kpiOrgScope', 'orgScope')
      .leftJoinAndSelect('stat.kpi', 'kpi')
      .where('stat.domain = :domainId', { domainId: domain.id })
      .andWhere('stat.periodType = :periodType', { periodType })
      .andWhere('stat.valueDate = :valueDate', { valueDate })
      .andWhere('orgScope.scope02 IS NOT NULL') // 지역 정보가 있는 것만
      .getMany()

    // 계산 결과가 없으면 새로 계산
    if (kpiStatistics.length === 0) {
      return await KpiStatisticCalculationService.calculateAllStatistics(
        periodType,
        valueDate,
        context
      )
    }

    // 기존 통계 재계산
    const results: KpiStatistic[] = []
    for (const stat of kpiStatistics) {
      const recalculated = await KpiStatisticCalculationService.calculateKpiStatistics(
        stat.kpiId,
        periodType,
        valueDate,
        stat.kpiOrgScope,
        context
      )
      if (recalculated) results.push(recalculated)
    }

    return results
  }
}
